import { Capability, FeatureFlag, IntegrationID } from '__types__';
import { maybe, rd } from '@passionware/monads';
import {
  ArrowLeft,
  BarChart3,
  Blocks,
  Building2,
  Coins,
  DoorOpen,
  Palette,
  Plus,
  Settings,
  Shapes,
  Shield,
  Tag,
  Type,
  Users
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { withinSettings } from 'v4/core/appRoutes';
import type { AppRouting } from 'v5/core/routing/appRouting';
import { AppScopeProvider } from 'v5/core/app-scope/_private/AppScopeProvider';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseWorkspaceSettingsCollapsible } from 'v5/features/core/enterprise/EnterpriseWorkspaceSettingsCollapsible';
import { useTranslation } from 'react-i18next';
import { useManageEnterpriseFeature } from 'v5/features/core/enterprise/useManageEnterpriseFeature';
import { Button } from 'v5/design-system/Button';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import { MenuItem, MenuSeparator } from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarSectionHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { cn } from 'v5/platform/dom/cn';
import { ifCan } from 'v5/platform/react/ifCan';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithDevOriginService } from 'v5/services/front/DevOriginService/DevOriginService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithIntegrationService } from 'v5/services/io/IntegrationService/IntegrationService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import {
  WithContentThemeService,
  WithThemeService
} from 'v5/services/front/ThemeService/ThemeService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import {
  isSettingsPath,
  SETTINGS_RETURN_TO_KEY
} from 'v5/core/routing/routeChangeEvent';

export interface EnterpriseSettingsSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithFeatureService,
      WithSupportService,
      WithThemeService,
      WithContentThemeService,
      WithOnlineStatusService,
      WithDevOriginService,
      WithEnterpriseService,
      WithUserFlowService,
      WithAppScopeService,
      WithAccessService,
      WithIntegrationService
    ]
  > {
  className?: string;
}

export function EnterpriseSettingsSidebar({
  services,
  className
}: EnterpriseSettingsSidebarProps) {
  const history = useHistory();
  // Slug scope is sync (route / Global provider). Do not wait on resolved RemoteData —
  // settings URLs omit org, so an empty fallback yields `/app//w//…` and blank chrome.
  const slugAppScope = services.appScopeService.useSlugAppScope();
  const organizationId = slugAppScope.organizationId;
  const legacyTeamSettings = withinSettings(organizationId);
  const orgRouting: AppRouting = appRouting.org(organizationId);
  const settings = orgRouting.settings;

  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId ? maybe.of(organizationId) : maybe.ofAbsent()
  );
  const workspaces = rd.tryGet(workspacesRd);
  const activeWorkspaceSlug = maybe.getOrElse(
    appScopeUtils.getWorkspaceSlug(slugAppScope),
    workspaces?.[0]?.slug ?? ''
  );

  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { t: tSettingsSidebar } = useTranslation('core', {
    keyPrefix: 'settingsSidebar'
  });
  const { t: tResourceTypes } = useTranslation('core', {
    keyPrefix: 'resourceTypesAdmin'
  });
  // Global chrome stays on organization identity — not the settings URL workspace.
  const orgCanRd = services.accessService.useCan(
    services.appScopeService.useOrganizationScopeIdentity()
  );
  const canManage = useManageEnterpriseFeature(
    services.featureService,
    services.appScopeService
  );
  const isVisitorEnabled = rd.tryGet(
    services.integrationService.useIntegration(
      organizationId || undefined,
      IntegrationID.VISITOR_MANAGEMENT
    )
  )?.enabled;
  // Only the fallback: leaving settings prefers wherever the user came from.
  const backToApp = activeWorkspaceSlug
    ? orgRouting.workspace(activeWorkspaceSlug).dashboard()
    : orgRouting.organization.dashboard();

  const handleBack = () => {
    const storedReturnTo = sessionStorage.getItem(SETTINGS_RETURN_TO_KEY);
    if (storedReturnTo && !isSettingsPath(storedReturnTo)) {
      history.push(storedReturnTo);
    } else {
      history.push(backToApp);
    }
  };

  const manageEnterpriseLink = canManage ? (
    <NavLink to={settings.global.manageEnterprise()}>
      <MenuItem icon={<Building2 className="size-4" />}>
        {t('manageEnterprise')}
      </MenuItem>
    </NavLink>
  ) : null;

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex items-center gap-2 min-h-(--header-height) px-2">
        <Button
          variant="ghost"
          size="sm"
          layout="icon"
          aria-label={tSettingsSidebar('backToApp')}
          onClick={handleBack}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="flex-1 text-fg-default">Settings</span>
        <DockableSidebarToggle>
          <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
        </DockableSidebarToggle>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        <SidebarGroup label="Your account">
          <NavLink to={settings.personal.account()}>
            <MenuItem icon={<Users className="size-4" />}>
              Personal account
            </MenuItem>
          </NavLink>
          <NavLink to={settings.personal.security()}>
            <MenuItem icon={<Shield className="size-4" />}>Security</MenuItem>
          </NavLink>
        </SidebarGroup>

        {ifCan
          .ready(orgCanRd)
          .render(can => (
            <SidebarGroup label="Global">
              {can({ action: Capability.CONFIGURE_TEAM }) && (
                <NavLink to={settings.global.users()}>
                  <MenuItem icon={<Users className="size-4" />}>Users</MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_TEAM }) && (
                <NavLink to={settings.global.permissionGroups()}>
                  <MenuItem icon={<Users className="size-4" />}>
                    Permission groups
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_AUTHENTICATION_METHODS }) && (
                <NavLink to={settings.global.authentication()}>
                  <MenuItem icon={<Shield className="size-4" />}>
                    Authentication
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_FIELDS }) && (
                <NavLink to={legacyTeamSettings.settings.team.fields()}>
                  <MenuItem icon={<Settings className="size-4" />}>
                    Custom fields
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_RESOURCE_TYPES }) && (
                <NavLink to={settings.global.resourceTypes()}>
                  <MenuItem icon={<Type className="size-4" />}>
                    {tResourceTypes('globalTitle')}
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_TAGS }) && (
                <NavLink to={settings.global.tags()}>
                  <MenuItem icon={<Tag className="size-4" />}>
                    {t('tags')}
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_RATE_CARDS }) && (
                <NavLink to={settings.global.rateCards()}>
                  <MenuItem icon={<Coins className="size-4" />}>
                    {t('rateCards')}
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_SLOT_CATEGORIES }) && (
                <NavLink to={settings.global.slotCategories()}>
                  <MenuItem icon={<Shapes className="size-4" />}>
                    {t('slotCategories')}
                  </MenuItem>
                </NavLink>
              )}
              {can({ action: Capability.CONFIGURE_REPORT_TEMPLATES }) &&
                can({ action: Capability.REPORT_DOWNLOAD }) && (
                  <NavLink to={settings.global.reportTemplates()}>
                    <MenuItem icon={<BarChart3 className="size-4" />}>
                      {t('reportTemplates')}
                    </MenuItem>
                  </NavLink>
                )}
              {(can({ feature: FeatureFlag.CUSTOM_SHOTFLOW }) ||
                can({ action: Capability.CONFIGURE_INTEGRATIONS })) && (
                <NavLink to={settings.global.integrations()}>
                  <MenuItem icon={<Blocks className="size-4" />}>
                    Integrations
                  </MenuItem>
                </NavLink>
              )}
              {isVisitorEnabled &&
                can({ action: Capability.MANAGE_VISITORS }) && (
                  <NavLink to={settings.global.visitorManagement()}>
                    <MenuItem icon={<DoorOpen className="size-4" />}>
                      {t('visitorManagement')}
                    </MenuItem>
                  </NavLink>
                )}
              {can({ action: Capability.CONFIGURE_WORKSPACE }) && (
                <NavLink to={settings.global.theming()}>
                  <MenuItem icon={<Palette className="size-4" />}>
                    {t('theming')}
                  </MenuItem>
                </NavLink>
              )}
              {manageEnterpriseLink}
            </SidebarGroup>
          ))
          .otherwise(
            manageEnterpriseLink ? (
              <SidebarGroup label="Global">{manageEnterpriseLink}</SidebarGroup>
            ) : null
          )}

        <MenuSeparator />
        <SidebarSectionHeader>Workspace settings</SidebarSectionHeader>
        {rd
          .journey(workspacesRd)
          .wait(<EnterpriseMenuCollapsibleSkeleton count={2} />)
          .catch(() => (
            <div className="px-3 py-2 text-[13px] text-fg-muted">
              {t('workspacesUnavailable')}
            </div>
          ))
          .map(workspacesList => (
            <>
              {workspacesList.map(workspace => (
                <AppScopeProvider
                  key={workspace.id}
                  scope={{
                    level: 'workspace',
                    organizationId,
                    workspaceSlug: workspace.slug
                  }}
                >
                  <EnterpriseWorkspaceSettingsCollapsible
                    services={services}
                    workspace={workspace}
                    organizationId={organizationId}
                    canManage={canManage}
                  />
                </AppScopeProvider>
              ))}
              {canManage ? (
                <MenuItem
                  className="text-fg-muted cursor-pointer"
                  icon={<Plus className="size-4" />}
                  onClick={event => {
                    event.preventDefault();
                    services.userFlowService.openCreateEnterpriseWorkspaceDialog(
                      organizationId,
                      { appScope: slugAppScope }
                    );
                  }}
                >
                  {t('newWorkspace')}
                </MenuItem>
              ) : null}
            </>
          ))}
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
